import React, { useState, useEffect, useRef, useCallback } from 'react'
import './App.css'
import { useAuth } from './hooks/useAuth'
import { LoginModal } from './components/LoginModal'

/* ═══════════════════════════════════════════════════ */
/* SVG ICONS                                           */
/* ═══════════════════════════════════════════════════ */

const ShieldCheckIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = '#00B5A3',
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
)

const UnlockIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 14,
  color = '#9CA3AF',
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
)

const SpinnerIcon: React.FC = () => (
  <svg
    className="spinner"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

/* ═══════════════════════════════════════════════════ */
/* INPUT MASKS                                         */
/* ═══════════════════════════════════════════════════ */

function maskCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 16)
  const groups = digits.match(/.{1,4}/g)
  return groups ? groups.join(' ') : ''
}

function maskExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4)
  if (digits.length > 2) {
    return digits.slice(0, 2) + '/' + digits.slice(2)
  }
  return digits
}

function maskCPF(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  let result = digits
  if (digits.length > 3) result = digits.slice(0, 3) + '.' + digits.slice(3)
  if (digits.length > 6)
    result = digits.slice(0, 3) + '.' + digits.slice(3, 6) + '.' + digits.slice(6)
  if (digits.length > 9)
    result =
      digits.slice(0, 3) +
      '.' +
      digits.slice(3, 6) +
      '.' +
      digits.slice(6, 9) +
      '-' +
      digits.slice(9)
  return result
}

function maskCEP(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length > 5) {
    return digits.slice(0, 5) + '-' + digits.slice(5)
  }
  return digits
}

/* ═══════════════════════════════════════════════════ */
/* CPF VALIDATION                                      */
/* ═══════════════════════════════════════════════════ */

function validateCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '')
  if (digits.length !== 11) return false
  if (/^(\d)\1{10}$/.test(digits)) return false

  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i)
  }
  let remainder = (sum * 10) % 11
  if (remainder === 10) remainder = 0
  if (remainder !== parseInt(digits[9])) return false

  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i)
  }
  remainder = (sum * 10) % 11
  if (remainder === 10) remainder = 0
  if (remainder !== parseInt(digits[10])) return false

  return true
}

/* ═══════════════════════════════════════════════════ */
/* TYPES                                               */
/* ═══════════════════════════════════════════════════ */

interface AddressData {
  street: string
  neighborhood: string
  city: string
  state: string
}

type ButtonState = 'default' | 'processing' | 'card-error' | 'server-error' | 'success'

/* ═══════════════════════════════════════════════════ */
/* APP COMPONENT                                       */
/* ═══════════════════════════════════════════════════ */

function App() {
  /* ─── Auth ─── */
  const { isAuthenticated, isLoading: authLoading, token, login } = useAuth()
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [countdown, setCountdown] = useState(10)
  const pendingSubmitRef = useRef(false)

  /* ─── Form state ─── */
  const [holderName, setHolderName] = useState('')
  const [cardNumber, setCardNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvv, setCvv] = useState('')
  const [cpf, setCpf] = useState('')
  const [cep, setCep] = useState('')
  const [addressNumber, setAddressNumber] = useState('')

  /* ─── Address from ViaCEP ─── */
  const addressRef = useRef<AddressData>({
    street: '',
    neighborhood: '',
    city: '',
    state: '',
  })

  /* ─── Validation errors ─── */
  const [errors, setErrors] = useState<Record<string, boolean>>({})

  /* ─── Button state ─── */
  const [buttonState, setButtonState] = useState<ButtonState>('default')
  const [feedbackMessage, setFeedbackMessage] = useState<string>('')

  /* ─── Refs for scroll ─── */
  const formRef = useRef<HTMLFormElement>(null)

  /* ─── CEP fetch ─── */
  const handleCepBlur = useCallback(async () => {
    const digits = cep.replace(/\D/g, '')
    if (digits.length !== 8) {
      setErrors((prev) => ({ ...prev, cep: true }))
      return
    }
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`)
      const data = await res.json()
      if (data.erro) {
        setErrors((prev) => ({ ...prev, cep: true }))
        return
      }
      addressRef.current = {
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
      }
      setErrors((prev) => ({ ...prev, cep: false }))
    } catch {
      setErrors((prev) => ({ ...prev, cep: true }))
    }
  }, [cep])

  /* ─── Validation ─── */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, boolean> = {}
    let valid = true

    if (!holderName.trim()) {
      newErrors.holderName = true
      valid = false
    }

    const cardDigits = cardNumber.replace(/\D/g, '')
    if (cardDigits.length !== 16) {
      newErrors.cardNumber = true
      valid = false
    }

    const expiryDigits = expiry.replace(/\D/g, '')
    if (expiryDigits.length !== 4) {
      newErrors.expiry = true
      valid = false
    } else {
      const month = parseInt(expiryDigits.slice(0, 2))
      const year = parseInt('20' + expiryDigits.slice(2))
      if (month < 1 || month > 12) {
        newErrors.expiry = true
        valid = false
      } else {
        const now = new Date()
        const expiryDate = new Date(year, month - 1, 1)
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        if (expiryDate < currentMonth) {
          newErrors.expiry = true
          valid = false
        }
      }
    }

    const cvvDigits = cvv.replace(/\D/g, '')
    if (cvvDigits.length !== 3) {
      newErrors.cvv = true
      valid = false
    }

    if (!validateCPF(cpf)) {
      newErrors.cpf = true
      valid = false
    }

    const cepDigits = cep.replace(/\D/g, '')
    if (cepDigits.length !== 8) {
      newErrors.cep = true
      valid = false
    }

    if (!addressNumber.trim()) {
      newErrors.addressNumber = true
      valid = false
    }

    setErrors(newErrors)

    if (!valid) {
      const firstErrorKey = Object.keys(newErrors).find((k) => newErrors[k])
      if (firstErrorKey && formRef.current) {
        const el = formRef.current.querySelector(`[data-field="${firstErrorKey}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }

    return valid
  }, [holderName, cardNumber, expiry, cvv, cpf, cep, addressNumber])

  /* ─── Submit handler ─── */
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault()
      if (buttonState === 'processing') return

      if (!validate()) return

      // Se não autenticado, abrir modal de login
      if (!isAuthenticated) {
        pendingSubmitRef.current = true
        setShowLoginModal(true)
        return
      }

      setFeedbackMessage('')
      setButtonState('processing')

      const PUBLIC_KEY = process.env.REACT_APP_PAGARME_PUBLIC_KEY || ''
      const API_URL = process.env.REACT_APP_API_URL || ''

      const cardDigits = cardNumber.replace(/\D/g, '')
      const expiryDigits = expiry.replace(/\D/g, '')
      const cpfDigits = cpf.replace(/\D/g, '')
      const cepDigits = cep.replace(/\D/g, '')

      /* Card tokenization */
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 10000)

        const tokenRes = await fetch(
          `https://api.pagar.me/core/v5/tokens?appId=${PUBLIC_KEY}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              type: 'card',
              card: {
                number: cardDigits,
                holder_name: holderName.trim(),
                holder_document: cpfDigits,
                exp_month: parseInt(expiryDigits.slice(0, 2)),
                exp_year: parseInt('20' + expiryDigits.slice(2)),
                cvv: cvv,
              },
            }),
          }
        )

        clearTimeout(timeout)

        if (!tokenRes.ok) {
          setButtonState('card-error')
          setFeedbackMessage('Dados do cartão inválidos. Verifique o número, validade e CVV.')
          setTimeout(() => setButtonState('default'), 5000)
          return
        }

        const tokenData = await tokenRes.json()
        const cardToken = tokenData.id

        if (!token) {
          setButtonState('server-error')
          setTimeout(() => setButtonState('default'), 3000)
          return
        }

        /* Backend call — authenticated endpoint */
        const subscribeUrl = API_URL
          ? `${API_URL}/api/subscribe`
          : '/api/subscribe'

        const subController = new AbortController()
        const subTimeout = setTimeout(() => subController.abort(), 10000)

        const subRes = await fetch(subscribeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          signal: subController.signal,
          body: JSON.stringify({
            card_token: cardToken,
            billing_address: {
              zip_code: cepDigits,
              number: addressNumber.trim(),
              street: addressRef.current.street,
              neighborhood: addressRef.current.neighborhood,
              city: addressRef.current.city,
              state: addressRef.current.state,
              country: 'BR',
            },
          }),
        })

        clearTimeout(subTimeout)

        if (subRes.status === 409) {
          setButtonState('default')
          alert('Você já possui uma assinatura ativa do Diana Premium.')
          return
        }

        if (!subRes.ok) {
          try {
            const errData = await subRes.json()
            if (errData?.error === 'card_declined' || errData?.error === 'customer_creation_failed' || errData?.error === 'missing_cpf') {
              setButtonState('card-error')
              setFeedbackMessage(errData?.message || 'Pagamento não aprovado. Verifique os dados do cartão e tente novamente.')
            } else {
              setButtonState('server-error')
              setFeedbackMessage('Não foi possível processar seu pagamento. Tente novamente em alguns instantes.')
            }
          } catch {
            setButtonState('server-error')
            setFeedbackMessage('Não foi possível processar seu pagamento. Tente novamente em alguns instantes.')
          }
          setTimeout(() => setButtonState('default'), 5000)
          return
        }

        /* Success */
        setFeedbackMessage('')
        setButtonState('success')
        setShowSuccess(true)
      } catch (err: any) {
        if (err.name === 'AbortError') {
          setButtonState('server-error')
          setFeedbackMessage('A conexão demorou demais. Verifique sua internet e tente novamente.')
          setTimeout(() => setButtonState('default'), 5000)
          return
        }
        setButtonState('server-error')
        setFeedbackMessage('Não foi possível processar seu pagamento. Tente novamente em alguns instantes.')
        setTimeout(() => setButtonState('default'), 5000)
      }
    },
    [
      isAuthenticated,
      token,
      buttonState,
      holderName,
      cardNumber,
      expiry,
      cvv,
      cpf,
      cep,
      addressNumber,
      validate,
    ]
  )

  /* ─── Auto-submit after login ─── */
  useEffect(() => {
    if (isAuthenticated && pendingSubmitRef.current) {
      pendingSubmitRef.current = false
      handleSubmit()
    }
  }, [isAuthenticated, handleSubmit])

  const handleLoginSuccess = useCallback(() => {
    setShowLoginModal(false)
    // pendingSubmitRef.current permanece true → useEffect dispara handleSubmit
    // quando isAuthenticated mudar para true no próximo render
  }, [])

  /* ─── Success countdown + redirect ─── */
  useEffect(() => {
    if (!showSuccess) return
    if (countdown <= 0) {
      const redirectUrl = (process.env.REACT_APP_CHECKOUT_URL || 'https://pro.testfy.com.br').split('?')[0]
      window.location.href = redirectUrl
      return
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [showSuccess, countdown])

  /* ─── Button label ─── */
  const renderButtonContent = () => {
    switch (buttonState) {
      case 'processing':
        return (
          <>
            <SpinnerIcon /> Processando teste...
          </>
        )
      case 'card-error':
        return 'Verifique os dados do cartão'
      case 'server-error':
        return 'Tente novamente'
      default:
        return 'Começar teste gratuito'
    }
  }

  const isSubmitDisabled = buttonState === 'processing' || authLoading

  /* ─── Scroll to checkout ─── */
  const scrollToCheckout = () => {
    const el = document.getElementById('checkout')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  /* ═══════════════════════════════════════════════════ */
  /* RENDER                                              */
  /* ═══════════════════════════════════════════════════ */

  return (
    <div className="page-wrapper">
      {/* ═══ HEADER ═══ */}
      <header className="site-header site-header--navy">
        <div className="container header-inner">
          <a href="https://pro.testfy.com.br" className="header-logo">
            <img src="/assets/images/testfy-white.svg" alt="Testfy" className="header-logo-img" />
          </a>
          <nav className="header-nav">
            <a href="https://pro.testfy.com.br" className="header-cta header-cta--light">Ir ao Testfy Pro</a>
          </nav>
        </div>
      </header>

      {/* ═══ SECTION 1 — HERO ═══ */}
      <section className="hero">
        <div className="container">
          {/* LEFT COLUMN */}
          <div className="hero-left">
            <div className="hero-top">
              <a
                href="https://pro.testfy.com.br"
                className="hero-top-link"
              >
                Testfy Professionals
              </a>
              <span className="hero-top-sep">&middot;</span>
              <span className="hero-pill">Nova experiência</span>
            </div>

            <h1 className="hero-title">
              Teste a Diana grátis por 7 dias.
            </h1>

            <span className="hero-byline">by <span className="hero-greenbook">GREENBOOK</span></span>

            <p className="hero-body">
              Você já conhece a Diana. Agora, experimente sem limites por 7
              dias: análises, chat completo e correlações clínicas de exames
              — tudo com a inteligência dos protocolos do Greenbook. Você só
              paga após o período de teste.
            </p>

            <p className="hero-tagline">
              Análises ilimitadas &middot; Chat clínico completo
              &middot; Cancele quando quiser
            </p>

            {/* Diana + bubbles */}
            <div className="hero-preview">
              <img
                src="/assets/images/diana.png"
                alt="Diana"
                className="hero-diana-img"
              />
              <div className="hero-bubbles">
                <div className="hero-bubble hero-bubble--user">
                  Como avaliar uma variante de risco moderado no metabolismo lipídico em paciente com síndrome metabólica?
                </div>
                <div className="hero-bubble hero-bubble--ai">
                  Variantes de risco moderado no metabolismo lipídico merecem atenção especial quando associadas a síndrome metabólica. O padrão sugere monitoramento trimestral do perfil lipídico, com ênfase em triglicerídeos e HDL. A combinação com intervenção nutricional personalizada tende a ser mais efetiva que abordagem farmacológica isolada nesse perfil.
                </div>
                <span className="hero-source-tag">Baseado em protocolos Greenbook</span>
              </div>
            </div>
            <p className="hero-preview-caption">Veja como a Diana analisa os relatórios dos seus pacientes — sem limites.</p>
          </div>

          {/* RIGHT COLUMN — CHECKOUT */}
          <div className="hero-right">
            <div className="checkout-card" id="checkout">
              {/* Header */}
              <div className="checkout-header">
                <span className="checkout-plan-name">Diana Premium</span>
                <span className="badge-recommended">Recomendado</span>
              </div>

              {/* Price */}
              <div className="checkout-price">
                <span className="price-value">R$ 197</span>
                <span className="price-period">/mês</span>
              </div>
              <div className="price-annual-note">cobrado anualmente</div>

              {/* Annual badge */}
              <div className="annual-badge">
                12x de R$ 197 no cartão · R$ 2.364/ano
              </div>

              {/* Trial */}
              <div className="guarantee-block">
                <div className="guarantee-title">
                  <ShieldCheckIcon size={16} color="#00B5A3" />
                  Teste grátis por 7 dias
                </div>
                <div className="guarantee-desc">
                  Você só paga após o período de teste. Cancele quando quiser antes da cobrança.
                </div>
              </div>

              {/* Bullets */}
              <div className="checkout-bullets">
                <div className="checkout-bullet">
                  <span className="bullet-dot" />
                  Análises ilimitadas
                </div>
                <div className="checkout-bullet">
                  <span className="bullet-dot" />
                  Chat completo com a Diana
                </div>
                <div className="checkout-bullet">
                  <span className="bullet-dot" />
                  Acesso antecipado a novas funcionalidades
                </div>
              </div>

              {/* Trust line */}
              <div className="trust-line">
                <UnlockIcon size={14} color="#9CA3AF" />
                Sem fidelidade. Cancele quando quiser.
              </div>

              {/* Form */}
              <form
                className="checkout-form"
                ref={formRef}
                onSubmit={handleSubmit}
                noValidate
              >
                {/* Dados do cartão */}
                <span className="form-section">Dados do cartão</span>
                <p className="trial-card-disclaimer" style={{ color: '#6B7280', fontSize: 13, margin: '0 0 8px' }}>
                  É necessário cadastrar um cartão para começar o teste.
                </p>

                <div className="form-row form-row-60-40">
                  <div className="form-field">
                    <label className="form-label">Nome no cartão</label>
                    <input
                      type="text"
                      className={`form-input${errors.holderName ? ' error' : ''}`}
                      placeholder="Como está no cartão"
                      value={holderName}
                      onChange={(e) => {
                        setHolderName(e.target.value)
                        setErrors((prev) => ({ ...prev, holderName: false }))
                      }}

                      data-field="holderName"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Número do cartão</label>
                    <input
                      type="text"
                      className={`form-input${errors.cardNumber ? ' error' : ''}`}
                      placeholder="0000 0000 0000 0000"
                      value={cardNumber}
                      onChange={(e) => {
                        setCardNumber(maskCardNumber(e.target.value))
                        setErrors((prev) => ({ ...prev, cardNumber: false }))
                      }}

                      inputMode="numeric"
                      data-field="cardNumber"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">Validade</label>
                    <input
                      type="text"
                      className={`form-input${errors.expiry ? ' error' : ''}`}
                      placeholder="MM/AA"
                      value={expiry}
                      onChange={(e) => {
                        setExpiry(maskExpiry(e.target.value))
                        setErrors((prev) => ({ ...prev, expiry: false }))
                      }}

                      inputMode="numeric"
                      data-field="expiry"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">CVV</label>
                    <input
                      type="text"
                      className={`form-input${errors.cvv ? ' error' : ''}`}
                      placeholder="3 dígitos"
                      value={cvv}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 3)
                        setCvv(digits)
                        setErrors((prev) => ({ ...prev, cvv: false }))
                      }}

                      inputMode="numeric"
                      data-field="cvv"
                    />
                  </div>
                </div>

                {/* Cobrança */}
                <span className="form-section">Identificação e endereço</span>

                <div className="form-row form-row-3">
                  <div className="form-field">
                    <label className="form-label">CPF</label>
                    <input
                      type="text"
                      className={`form-input${errors.cpf ? ' error' : ''}`}
                      placeholder="000.000.000-00"
                      value={cpf}
                      onChange={(e) => {
                        setCpf(maskCPF(e.target.value))
                        setErrors((prev) => ({ ...prev, cpf: false }))
                      }}
                      inputMode="numeric"
                      data-field="cpf"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">CEP</label>
                    <input
                      type="text"
                      className={`form-input${errors.cep ? ' error' : ''}`}
                      placeholder="00000-000"
                      value={cep}
                      onChange={(e) => {
                        setCep(maskCEP(e.target.value))
                        setErrors((prev) => ({ ...prev, cep: false }))
                      }}
                      onBlur={handleCepBlur}
                      inputMode="numeric"
                      data-field="cep"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Numero</label>
                    <input
                      type="text"
                      className={`form-input${errors.addressNumber ? ' error' : ''}`}
                      placeholder="Ex: 42"
                      value={addressNumber}
                      onChange={(e) => {
                        setAddressNumber(e.target.value)
                        setErrors((prev) => ({ ...prev, addressNumber: false }))
                      }}
                      data-field="addressNumber"
                    />
                  </div>
                </div>

                {/* Security line */}
                <div className="security-line">
                  <ShieldCheckIcon size={14} color="#9CA3AF" />
                  Pagamento seguro com criptografia
                </div>

                {/* Legal notice */}
                <div className="legal-notice">
                  Ao confirmar, você autoriza a cobrança de 12x de R$ 197,00, com
                  renovação automática após 12 meses.
                </div>

                {/* Value anchor */}
                <div className="value-anchor-text">
                  Menos de R$ 6,55 por dia para elevar o padrão de cada
                  atendimento.
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitDisabled}
                >
                  {renderButtonContent()}
                </button>

                <p className="trial-disclaimer" style={{ color: '#6B7280', fontSize: 13, margin: '10px 0 0', textAlign: 'center', lineHeight: 1.5 }}>
                  Você só será cobrado após o período de teste de 7 dias. Cancele quando quiser antes da cobrança.
                </p>

                {feedbackMessage && (buttonState === 'card-error' || buttonState === 'server-error') && (
                  <div className={`payment-feedback ${buttonState === 'card-error' ? 'payment-feedback--card' : 'payment-feedback--server'}`}>
                    <span className="payment-feedback__icon">
                      {buttonState === 'card-error' ? '⚠' : 'ℹ'}
                    </span>
                    <span className="payment-feedback__text">{feedbackMessage}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2 — O QUE A DIANA PREMIUM OFERECE ═══ */}
      <section className="features">
        <div className="container">
          <h2 className="features-title">
            Tudo o que você precisa para elevar cada atendimento
          </h2>
          <p className="features-subtitle">
            Inteligência clínica completa, integrada aos protocolos do
            Greenbook — sem limites de uso.
          </p>

          <div className="features-content">
            {/* LEFT — Grid */}
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-card-dot" />
                <p className="feature-card-text">
                  Base de 22 milhões de artigos científicos, todos com o maior rigor de validação para sua segurança
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-card-dot" />
                <p className="feature-card-text">
                  Lê relatórios e raciocina com o histórico completo do paciente
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-card-dot" />
                <p className="feature-card-text">
                  Discussão clínica livre — como conversar com um colega sênior
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-card-dot" />
                <p className="feature-card-text">
                  Memória da consulta — aprofunde, questione e peça alternativas
                  sem perder contexto
                </p>
              </div>
            </div>

            {/* RIGHT — Timeline */}
            <div className="features-timeline">
              <div className="timeline-line" />
              <div className="timeline-step">
                <div className="timeline-circle">1</div>
                <p className="timeline-text">
                  Acesse a análise e clique em &ldquo;Analisar com IA&rdquo;
                </p>
              </div>
              <div className="timeline-step">
                <div className="timeline-circle">2</div>
                <p className="timeline-text">
                  A Diana cruza dados, ficha clínica e histórico do paciente
                </p>
              </div>
              <div className="timeline-step">
                <div className="timeline-circle">3</div>
                <p className="timeline-text">
                  Você recebe uma análise aprofundada e personalizada
                </p>
              </div>
              <div className="timeline-step">
                <div className="timeline-circle">4</div>
                <p className="timeline-text">
                  O chat se abre para você explorar qualquer ponto com
                  profundidade
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3 — STATS ═══ */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">22M+</div>
              <div className="stat-label">artigos científicos</div>
            </div>
            <div className="stat-separator" />
            <div className="stat-item">
              <div className="stat-value">100%</div>
              <div className="stat-label">contextualizado ao paciente</div>
            </div>
            <div className="stat-separator" />
            <div className="stat-item">
              <div className="stat-value">Tempo real</div>
              <div className="stat-label">evidências atualizadas</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4 — COMPARATIVO ═══ */}
      <section className="plans">
        <div className="container">
          <h2 className="plans-title">Veja o que muda com a Diana Premium</h2>
          <p className="plans-subtitle">
            Você já usa o plano gratuito. Compare e descubra o que o
            Premium desbloqueia.
          </p>

          <div className="plans-grid">
            {/* BASIC */}
            <div className="plan-card-basic">
              <div className="plan-name">Seu plano atual</div>
              <div className="plan-desc">
                O básico para conhecer a Diana.
              </div>
              <div className="plan-bullets">
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  Acesso à plataforma Testfy Pro
                </div>
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  3 interações por análise
                </div>
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  Não é possível enviar anexos e cruzar exames simples
                </div>
              </div>
            </div>

            {/* PREMIUM */}
            <div className="plan-card-premium">
              <span className="plan-badge">Recomendado</span>
              <div className="plan-name">Diana Premium</div>
              <div className="plan-price">
                <span className="plan-price-value">R$ 197</span>
                <span className="plan-price-period">/mês</span>
              </div>
              <div className="plan-annual-text">
                cobrado anualmente &middot; R$ 2.364/ano
              </div>
              <div className="plan-desc">
                Chat clínico sem limite de interações, com acesso ao
                histórico do paciente, base científica atualizada e análise
                de imagens — tudo em linguagem natural.
              </div>
              <div className="plan-bullets">
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  Análises ilimitadas
                </div>
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  Chat completo com a Diana, em qualquer análise
                </div>
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  Acesso antecipado a novas funcionalidades
                </div>
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  Potencial desbloqueado para cruzar dados com exames laboratoriais
                </div>
                <div className="plan-bullet">
                  <span className="plan-bullet-dot" />
                  Teste grátis por 7 dias · Cancele quando quiser
                </div>
              </div>
              <div className="plan-value-anchor">
                Menos de R$ 6,55 por dia para elevar o padrão de cada
                atendimento.
              </div>
              <button
                type="button"
                className="plan-cta-btn"
                onClick={scrollToCheckout}
              >
                Começar teste gratuito
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5 — FOOTER ═══ */}
      <footer className="footer">
        <div className="container">
          <div className="footer-governance">
            <ShieldCheckIcon size={14} color="#9CA3AF" />
            <span className="footer-governance-text">
              A Diana utiliza inteligência artificial como ferramenta de apoio.
              Toda decisão clínica é de responsabilidade do profissional de
              saúde.
            </span>
          </div>
        </div>
      </footer>

      {/* Success Overlay */}
      {showSuccess && (
        <div className="success-overlay">
          <div className="success-modal">
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#00B5A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h3 className="success-title">Seu trial começou!</h3>
            <p className="success-text">
              Você tem 7 dias de acesso completo à Diana. Você só será cobrado após o período de teste.
            </p>
            <p className="success-redirect">
              Redirecionando para o Testfy Professionals em <strong>{countdown}s</strong>
            </p>
            <div className="success-bar-track">
              <div className="success-bar-fill" style={{ width: `${((10 - countdown) / 10) * 100}%` }} />
            </div>
            <button
              type="button"
              className="success-btn"
              onClick={() => {
                const redirectUrl = (process.env.REACT_APP_CHECKOUT_URL || 'https://pro.testfy.com.br').split('?')[0]
                window.location.href = redirectUrl
              }}
            >
              Ir agora
            </button>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal
          onLogin={login}
          onClose={() => {
            setShowLoginModal(false)
            pendingSubmitRef.current = false
          }}
          onSuccess={handleLoginSuccess}
        />
      )}
    </div>
  )
}

export default App
